import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DoneIcon from "../../assets/images/actions/DoneIcon";
import { useAuth } from "../../context/AuthContext";
import styles from "./CountriesCities.module.scss";
import Button from "../../components/common/button/Button";
import PlusIcon from "../../assets/images/actions/PlusIcon";
import LocationsService from "../../api/services/locations.service";
import Delete from "../../assets/images/actions/Delete";
import { toast } from "sonner";
import DeleteModal from "../../components/common/delete-modal/DeleteModal";
import Loading from "../../components/common/loading/Loading";
import { Country, City } from "../../api/types/locations.types";
import TitleUpdater from "../../components/common/title/TitleUpdater";

interface Errors {
  name?: string;
  country_id?: string;
}

const CountriesCities = () => {
  const { t } = useTranslation("translation");
  const { setButtons } = useAuth();
  const [countries, setCountries] = useState<Country[]>([]);
  const [cities, setCities] = useState<Record<number, City[]>>({});
  const [expandedCountries, setExpandedCountries] = useState<Set<number>>(
    new Set(),
  );
  const [isVisibleCountry, setIsVisibleCountry] = useState(false);
  const [isVisibleCity, setIsVisibleCity] = useState<number | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState<"country" | "city">("country");
  const [formData, setFormData] = useState({ name: "", country_id: "" });
  const [errors, setErrors] = useState<Partial<Errors>>({});
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const getCountries = async () => {
    setLoading(true);
    try {
      const res = await LocationsService.getAllCountries(1, 100);
      setCountries(res.data.data || []);
    } catch (error) {
      console.error(error);
      toast.error(t("locations.messages.failed_to_load_countries"));
    } finally {
      setLoading(false);
    }
  };

  const getCities = async (countryId: number) => {
    try {
      const res = await LocationsService.getAllCities(countryId, 1, 100);
      setCities((prev) => ({ ...prev, [countryId]: res.data.data || [] }));
    } catch (error) {
      console.error(error);
      toast.error(t("locations.messages.failed_to_load_cities"));
    }
  };

  useEffect(() => {
    getCountries();
  }, []);

  const toggleCountry = (countryId: number) => {
    setExpandedCountries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(countryId)) {
        newSet.delete(countryId);
      } else {
        newSet.add(countryId);
        if (!cities[countryId]) {
          getCities(countryId);
        }
      }
      return newSet;
    });
  };

  const validateForm = (isCity: boolean = false): boolean => {
    const newErrors: Partial<Errors> = {};
    if (!formData.name.trim())
      newErrors.name = t("locations.errors.name_required") as string;
    if (isCity && !formData.country_id.trim()) {
      newErrors.country_id = t("locations.errors.country_required") as string;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddCountry = async () => {
    if (!validateForm(false)) return;
    try {
      const data = new FormData();
      data.append("name", formData.name);
      await LocationsService.createCountry(data);
      toast.success(t("locations.messages.country_added_successfully"));
      setFormData({ name: "", country_id: "" });
      setIsVisibleCountry(false);
      getCountries();
    } catch (error: any) {
      console.error(error);
      toast.error(
        error.response?.data?.error ||
          t("locations.messages.failed_to_add_country"),
      );
    }
  };

  const handleAddCity = async (countryId: number) => {
    if (!validateForm(true)) return;
    try {
      const data = new FormData();
      data.append("name", formData.name);
      data.append("country_id", countryId.toString());
      await LocationsService.createCity(data);
      toast.success(t("locations.messages.city_added_successfully"));
      setFormData({ name: "", country_id: "" });
      setIsVisibleCity(null);
      getCities(countryId);
    } catch (error: any) {
      console.error(error);
      toast.error(
        error.response?.data?.error ||
          t("locations.messages.failed_to_add_city"),
      );
    }
  };

  const handleInlineEditCountry = async (id: number, newValue: string) => {
    try {
      const data = new FormData();
      data.append("name", newValue);
      await LocationsService.updateCountry(id, data);
      toast.success(t("locations.messages.country_updated_successfully"));
      getCountries();
    } catch (error: any) {
      console.error(error);
      toast.error(
        error.response?.data?.error || t("locations.messages.update_failed"),
      );
    }
  };

  const handleInlineEditCity = async (
    id: number,
    countryId: number,
    newValue: string,
  ) => {
    try {
      const data = new FormData();
      data.append("name", newValue);
      await LocationsService.updateCity(id, data);
      toast.success(t("locations.messages.city_updated_successfully"));
      getCities(countryId);
    } catch (error: any) {
      console.error(error);
      toast.error(
        error.response?.data?.error || t("locations.messages.update_failed"),
      );
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteType === "country") {
        await LocationsService.deleteCountry(Number(deleteId));
        toast.success(t("locations.messages.country_deleted_successfully"));
        getCountries();
        setCities((prev) => {
          const newCities = { ...prev };
          delete newCities[Number(deleteId)];
          return newCities;
        });
      } else {
        const countryId = countries.find((c) =>
          cities[c.id]?.some((city) => city.id === Number(deleteId)),
        )?.id;
        if (countryId) {
          await LocationsService.deleteCity(Number(deleteId));
          toast.success(t("locations.messages.city_deleted_successfully"));
          getCities(countryId);
        }
      }
      setDeleteModal(false);
    } catch (error: any) {
      toast.error(
        error.response?.data?.error || t("locations.messages.failed_to_delete"),
      );
    }
  };

  useEffect(() => {
    setButtons([
      <Button
        icon={<PlusIcon />}
        key="add-country"
        onClick={() => {
          setIsVisibleCountry(true);
          setFormData({ name: "", country_id: "" });
          setErrors({});
        }}
      >
        {t("locations.add_country")}
      </Button>,
    ]);
    return () => setButtons([]);
  }, [setButtons]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.countries_cities}>
      <TitleUpdater title={t("locations.title")} />
      <div className={styles.header}>
        <h1 className={styles.title}>{t("locations.title")}</h1>
        <p className={styles.subtitle}>{t("locations.subtitle")}</p>
      </div>

      {isVisibleCountry && (
        <div className={styles.country_item}>
          <div className={styles.box}>
            <div className={styles.input_control}>
              <input
                className={`${styles.input} ${
                  errors.name ? styles.input_error : ""
                }`}
                type="text"
                placeholder={
                  t("locations.placeholders.enter_country_name") as string
                }
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
              {errors.name && <p className={styles.error}>{errors.name}</p>}
            </div>
            <div
              className={styles.done}
              onClick={handleAddCountry}
              title={t("locations.save_country") as string}
            >
              <DoneIcon />
            </div>
          </div>
        </div>
      )}

      {countries.map((country) => (
        <div key={country.id} className={styles.country_item}>
          <div className={styles.country_header}>
            <div className={styles.box}>
              <div>
                <input
                  className={styles.input}
                  type="text"
                  defaultValue={country.name}
                  onBlur={(e) => {
                    const newValue = e.target.value.trim();
                    if (newValue && newValue !== country.name)
                      handleInlineEditCountry(country.id, newValue);
                  }}
                />
              </div>
            </div>
            <div className={styles.icons}>
              <button
                className={styles.expand_btn}
                onClick={() => toggleCountry(country.id)}
                title={
                  expandedCountries.has(country.id)
                    ? (t("locations.collapse") as string)
                    : (t("locations.expand") as string)
                }
              >
                {expandedCountries.has(country.id) ? "▼" : "▶"}
              </button>
              <button
                onClick={() => {
                  setDeleteId(country.id);
                  setDeleteType("country");
                  setDeleteModal(true);
                }}
              >
                <Delete />
              </button>
            </div>
          </div>

          {expandedCountries.has(country.id) && (
            <div className={styles.cities_section}>
              {isVisibleCity === country.id && (
                <div className={styles.city_item}>
                  <div className={styles.box}>
                    <div className={styles.input_control}>
                      <input
                        className={`${styles.input} ${
                          errors.name ? styles.input_error : ""
                        }`}
                        type="text"
                        placeholder={
                          t("locations.placeholders.enter_city_name") as string
                        }
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                      />
                      {errors.name && (
                        <p className={styles.error}>{errors.name}</p>
                      )}
                    </div>
                    <div
                      className={styles.done}
                      onClick={() => handleAddCity(country.id)}
                      title={t("locations.save_city") as string}
                    >
                      <DoneIcon />
                    </div>
                  </div>
                </div>
              )}

              <Button
                icon={<PlusIcon />}
                className={styles.add_city_btn}
                onClick={() => {
                  setIsVisibleCity(country.id);
                  setFormData({ name: "", country_id: country.id.toString() });
                  setErrors({});
                }}
              >
                {t("locations.add_city")}
              </Button>

              {cities[country.id]?.map((city) => (
                <div key={city.id} className={styles.city_item}>
                  <div className={styles.box}>
                    <div>
                      <input
                        className={styles.input}
                        type="text"
                        defaultValue={city.name}
                        onBlur={(e) => {
                          const newValue = e.target.value.trim();
                          if (newValue && newValue !== city.name)
                            handleInlineEditCity(city.id, country.id, newValue);
                        }}
                      />
                    </div>
                  </div>
                  <div className={styles.icons}>
                    <button
                      onClick={() => {
                        setDeleteId(city.id);
                        setDeleteType("city");
                        setDeleteModal(true);
                      }}
                    >
                      <Delete />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <DeleteModal
        deleteModal={deleteModal}
        onClose={() => setDeleteModal(false)}
        onSubmit={handleDelete}
        title={
          (deleteType === "country"
            ? t("locations.delete_country")
            : t("locations.delete_city")) as string
        }
      />
    </div>
  );
};

export default CountriesCities;
